class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.stage = this.querySelector('[data-product-gallery-stage]');
    if (!this.stage) return;
    this.items = [...this.stage.querySelectorAll('.product-gallery__item[data-media-id]')];
    this.openers = [...this.querySelectorAll('[data-product-gallery-open]')];
    this.thumbnails = [...this.querySelectorAll('[data-product-gallery-thumbnail]')];
    this.dialog = this.querySelector('[data-product-gallery-dialog]');
    this.image = this.querySelector('[data-product-gallery-image]');
    this.caption = this.querySelector('[data-product-gallery-caption]');
    this.counter = this.querySelector('[data-product-gallery-counter]');
    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClose = this.onClose.bind(this);
    this.addEventListener('click', this.onClick);
    this.stage.addEventListener('scroll', this.onScroll, { passive: true });
    this.dialog?.addEventListener('keydown', this.onKeydown);
    this.dialog?.addEventListener('close', this.onClose);
    if (this.dialog?.id) {
      this.openers.forEach((opener) => {
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-controls', this.dialog.id);
      });
    }
    this.initialized = true;
    this.syncThumbnail();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.stage?.removeEventListener('scroll', this.onScroll);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('close', this.onClose);
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.initialized = false;
  }

  onClick(event) {
    const thumbnail = event.target.closest('[data-product-gallery-thumbnail]');
    if (thumbnail) {
      const item = this.items.find((candidate) => candidate.dataset.mediaId === thumbnail.dataset.mediaId);
      if (!item) return;
      event.preventDefault();
      item.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      this.syncThumbnail(thumbnail.dataset.mediaId);
      return;
    }

    const opener = event.target.closest('[data-product-gallery-open]');
    if (opener && typeof this.dialog?.showModal === 'function') {
      event.preventDefault();
      this.open(this.openers.indexOf(opener), opener);
      return;
    }
    if (event.target.closest('[data-product-gallery-close]')) this.dialog?.close();
    if (event.target.closest('[data-product-gallery-previous]')) this.move(-1);
    if (event.target.closest('[data-product-gallery-next]')) this.move(1);
  }

  onScroll() {
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.syncThumbnail();
      this.scrollFrame = null;
    });
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.move(-1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.move(1);
    }
  }

  onClose() {
    this.image?.removeAttribute('src');
    this.lastTrigger?.focus();
  }

  syncThumbnail(mediaId) {
    let activeId = mediaId;
    if (!activeId && this.items.length) {
      const stageBounds = this.stage.getBoundingClientRect();
      const stageCenter = stageBounds.left + (stageBounds.width / 2);
      const active = this.items.reduce((closest, item) => {
        const bounds = item.getBoundingClientRect();
        const distance = Math.abs((bounds.left + (bounds.width / 2)) - stageCenter);
        return distance < closest.distance ? { item, distance } : closest;
      }, { item: this.items[0], distance: Infinity }).item;
      activeId = active.dataset.mediaId;
    }
    this.thumbnails.forEach((thumbnail) => {
      if (thumbnail.dataset.mediaId === activeId) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });
  }

  open(index, trigger) {
    if (!this.openers.length || !this.dialog || !this.image) return;
    this.index = Math.max(0, index);
    this.lastTrigger = trigger;
    this.render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  move(direction) {
    if (this.openers.length < 2) return;
    this.index = (this.index + direction + this.openers.length) % this.openers.length;
    this.render();
  }

  render() {
    const opener = this.openers[this.index];
    if (!opener || !this.image) return;
    this.image.src = opener.dataset.gallerySrc;
    this.image.alt = opener.dataset.galleryAlt || '';
    this.image.width = Number(opener.dataset.galleryWidth || 1600);
    this.image.height = Number(opener.dataset.galleryHeight || 2000);
    if (this.caption) {
      this.caption.textContent = opener.dataset.galleryCaption || '';
      this.caption.hidden = !this.caption.textContent;
    }
    if (this.counter) {
      const template = this.dialog.dataset.positionLabel || '__CURRENT__ of __TOTAL__';
      this.counter.textContent = template.replace('__CURRENT__', String(this.index + 1)).replace('__TOTAL__', String(this.openers.length));
    }
  }
}

if (!customElements.get('product-media-gallery')) customElements.define('product-media-gallery', ProductMediaGallery);

class StickyPurchase extends HTMLElement {
  connectedCallback() {
    if (this.initialized || !('IntersectionObserver' in window)) return;
    this.primaryButton = this.closest('form')?.querySelector('[data-primary-add-to-cart]');
    if (!this.primaryButton) return;
    this.onIntersect = this.onIntersect.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
    this.observer.observe(this.primaryButton);
    this.desktopMedia.addEventListener?.('change', this.onMediaChange);
    this.addEventListener('click', this.onClick);
    this.hidden = false;
    this.setVisible(false);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.desktopMedia?.removeEventListener?.('change', this.onMediaChange);
    this.removeEventListener('click', this.onClick);
    this.initialized = false;
  }

  onIntersect(entries) {
    const entry = entries[0];
    if (!entry) return;
    this.shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
    this.setVisible(this.shouldShow);
  }

  onMediaChange() {
    this.setVisible(this.shouldShow);
  }

  onClick(event) {
    if (!event.target.closest('[data-sticky-purchase-dismiss]')) return;
    this.dismissed = true;
    this.setVisible(false);
  }

  setVisible(show) {
    const desktopAllowed = this.dataset.showDesktop === 'true' || !this.desktopMedia.matches;
    const visible = Boolean(show && desktopAllowed && !this.dismissed);
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    if (visible) this.removeAttribute('inert');
    else this.setAttribute('inert', '');
  }
}

if (!customElements.get('sticky-purchase')) customElements.define('sticky-purchase', StickyPurchase);

class ProductStoryNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.links = [...this.querySelectorAll('[data-product-story-link]')];
    this.items = this.links.flatMap((link) => {
      const hash = link.getAttribute('href');
      const target = hash?.startsWith('#') ? document.getElementById(hash.slice(1)) : null;
      if (!target) {
        const listItem = link.closest('li');
        if (listItem) listItem.hidden = true;
        return [];
      }
      return [{ hash, link, target }];
    });
    this.initialized = true;
    if (this.items.length < 2) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.visibleTargets = new Set();
    this.onIntersect = this.onIntersect.bind(this);
    this.onHashChange = this.onHashChange.bind(this);
    window.addEventListener('hashchange', this.onHashChange);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '-18% 0px -68% 0px',
        threshold: [0, 0.01],
      });
      this.items.forEach(({ target }) => this.observer.observe(target));
    }
    this.syncCurrent();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('hashchange', this.onHashChange);
    this.visibleTargets?.clear();
    this.initialized = false;
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.visibleTargets.add(entry.target);
      else this.visibleTargets.delete(entry.target);
    });
    this.syncCurrent();
  }

  onHashChange() {
    this.syncCurrent(window.location.hash);
  }

  syncCurrent(preferredHash = '') {
    let current = preferredHash && this.items.find(({ hash }) => hash === preferredHash);
    if (!current) current = this.items.find(({ target }) => this.visibleTargets?.has(target));
    if (!current) {
      const marker = (window.innerHeight || document.documentElement.clientHeight || 800) * 0.3;
      current = [...this.items].reverse().find(({ target }) => target.getBoundingClientRect().top <= marker)
        || this.items.find(({ target }) => target.getBoundingClientRect().bottom > marker)
        || this.items[0];
    }
    this.items.forEach(({ hash, link }) => {
      if (hash === current.hash) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('product-story-navigation')) customElements.define('product-story-navigation', ProductStoryNavigation);
